/**
 * CreateClassModal — admin-only. Creates a new school class and assigns
 * a teacher in one step. If the selected teacher isn't yet registered
 * as a school_teachers row, we auto-register them (idempotent) before
 * creating the class.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useCreateClass,
  useEligibleTeachers,
  useRegisterTeacher,
  useSchool,
  useSubjects,
} from "../../api/school";

interface Props {
  onClose: () => void;
  /** 学校管理页跨校建班(PRD 任务9):指定目标学校时用它做 school_id,
   *  并隐藏老师选择——register_teacher 端点固定落默认学校,跨校指派会
   *  注错学校;新学校先建"未指派"班,老师经邀请/调动入校后再挂。 */
  schoolId?: string;
}

export function CreateClassModal({ onClose, schoolId }: Props) {
  const { t } = useTranslation();
  const school = useSchool();
  const teachers = useEligibleTeachers();
  const createClass = useCreateClass();
  const registerTeacher = useRegisterTeacher();

  const [name, setName] = useState("");
  const [grade, setGrade] = useState("G7");
  const [section, setSection] = useState("A");
  const [semester, setSemester] = useState("2026-spring");
  // 学科:默认跟任课老师(Jarod 2026-08-11);GPA 课程班级(七年级1班·地理)需要显式指定。
  const [subjectSlug, setSubjectSlug] = useState("");
  const subjects = useSubjects();
  // user_ids of the selected nova_users rows (empty = unassigned).
  // Multi-teacher per migration 018; admin can pick 0..N teachers and
  // every entry has equal rights against the class.
  const [pickedUserIds, setPickedUserIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [err, setErr] = useState<string | null>(null);

  function toggleTeacher(userId: string) {
    setPickedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  async function submit() {
    const targetSchoolId = schoolId ?? school.data?.id;
    if (!targetSchoolId) {
      setErr(t("classes.createModal.errSchool"));
      return;
    }
    if (!name.trim()) {
      setErr(t("classes.createModal.errName"));
      return;
    }
    setErr(null);
    try {
      // Resolve each picked user_id to a school_teachers.id. Any user
      // not yet registered as a teacher is registered first
      // (idempotent — server returns the existing teacher_id if there's
      // already a row for that nova user).
      const teacherIds: string[] = [];
      for (const userId of pickedUserIds) {
        const picked = teachers.data?.find((tt) => tt.user_id === userId);
        if (picked?.teacher_id) {
          teacherIds.push(picked.teacher_id);
        } else {
          const r = await registerTeacher.mutateAsync(userId);
          teacherIds.push(r.teacher_id);
        }
      }
      await createClass.mutateAsync({
        school_id: targetSchoolId,
        name: name.trim(),
        grade: grade.trim(),
        section: section.trim() || null,
        // 学科默认跟任课老师走(Jarod 2026-08-11);显式选了就按选的建课程班级。
        ...(subjectSlug ? { subject: subjectSlug } : {}),
        teacher_ids: teacherIds,
        semester: semester.trim(),
      });
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  const busy = createClass.isPending || registerTeacher.isPending;

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-800">{t("classes.createModal.title")}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-xl leading-none"
          >
            ×
          </button>
        </header>

        <div className="p-6 space-y-3">
          <label className="block text-sm">
            <span className="block text-xs text-slate-600 mb-1">{t("classes.createModal.name")}</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("classes.createModal.namePlaceholder")}
              className="w-full px-2 py-1.5 rounded border border-slate-200"
            />
          </label>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="block text-xs text-slate-600 mb-1">{t("classes.createModal.grade")}</span>
              <input
                type="text"
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                placeholder="G7"
                className="w-full px-2 py-1.5 rounded border border-slate-200"
              />
            </label>
            <label className="text-sm">
              <span className="block text-xs text-slate-600 mb-1">{t("classes.createModal.section")}</span>
              <input
                type="text"
                value={section}
                onChange={(e) => setSection(e.target.value)}
                placeholder="A"
                className="w-full px-2 py-1.5 rounded border border-slate-200"
              />
            </label>
          </div>

          <label className="block text-sm">
            <span className="block text-xs text-slate-600 mb-1">{t("classes.createModal.subject")}</span>
            <select value={subjectSlug} onChange={(e) => setSubjectSlug(e.target.value)} className="w-full px-2 py-1.5 rounded border border-slate-200" data-testid="create-class-subject">
              <option value="">{t("classes.createModal.subjectByTeacher")}</option>
              {(subjects.data ?? []).filter((x) => x.status !== "archived").map((x) => <option key={x.slug} value={x.slug}>{x.name}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <span className="block text-xs text-slate-600 mb-1">{t("classes.createModal.semester")}</span>
            <input
              type="text"
              value={semester}
              onChange={(e) => setSemester(e.target.value)}
              placeholder={t("classes.createModal.semesterPlaceholder")}
              className="w-full px-2 py-1.5 rounded border border-slate-200"
            />
          </label>

          {schoolId ? (
            <p className="text-xs text-slate-500 rounded border border-slate-200 bg-slate-50 px-3 py-2">
              {t("classes.createModal.crossSchoolTeacherHint")}
            </p>
          ) : (
          <div className="block text-sm">
            <span className="block text-xs text-slate-600 mb-1">
              {t("classes.createModal.teachers")}
            </span>
            {teachers.isLoading ? (
              <p className="text-xs text-slate-500 italic">{t("common.loading")}</p>
            ) : (teachers.data?.length ?? 0) === 0 ? (
              <p className="text-xs text-slate-500">
                {t("classes.createModal.teacherEmpty")}
              </p>
            ) : (
              <div className="max-h-44 overflow-y-auto rounded border border-slate-200 divide-y divide-slate-100">
                {teachers.data?.map((tt) => (
                  <label
                    key={tt.user_id}
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={pickedUserIds.has(tt.user_id)}
                      onChange={() => toggleTeacher(tt.user_id)}
                    />
                    <span className="text-sm text-slate-800 flex-1 truncate">
                      {tt.display_name}{" "}
                      <span className="text-xs text-slate-500">({tt.email})</span>
                    </span>
                    {tt.role !== "employee" && (
                      <span className="text-[10px] font-mono text-slate-500">
                        {tt.role}
                      </span>
                    )}
                    {!tt.teacher_id && (
                      <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 rounded">
                        {t("classes.createModal.isNew")}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}
            <p className="text-[11px] text-slate-500 mt-1">
              {t("classes.createModal.teachersHint", { count: pickedUserIds.size })}
            </p>
          </div>
          )}

          {err && <div className="text-xs text-red-600">{err}</div>}
        </div>

        <footer className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 text-sm text-slate-600 rounded hover:bg-slate-100 disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !name.trim()}
            className="px-4 py-1.5 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
          >
            {busy ? t("classes.createModal.submitting") : t("classes.createModal.submit")}
          </button>
        </footer>
      </div>
    </div>
  );
}
